import { type ReactNode, useCallback, useMemo } from "react";
import { filled } from "~/util";

type Conditional = ReactNode | ArrayLike<unknown> | unknown | (() => boolean);

export type CallbackOnlyProps = {
    show: () => ReactNode;
    otherwise?: ReactNode | (() => ReactNode);
    children?: ReactNode;
};

export type ChildrenOnlyProps = {
    show?: never;
    otherwise?: ReactNode | (() => ReactNode);
    children: ReactNode;
};

export type NeverShowProps = {
    when: false | (() => false);
    show?: never;
    otherwise?: never;
    children?: never;
};

export type PermissibleProps = CallbackOnlyProps | ChildrenOnlyProps;

export type OnlyProps = {
    not?: boolean;
    when: Conditional;
    otherwise?: ReactNode | (() => ReactNode);
    show?: () => ReactNode;
    children?: ReactNode;
} & (PermissibleProps | NeverShowProps);

export const Only = ({ not = false, when, otherwise, show, children }: OnlyProps) => {
    const should_show = useMemo(() => filled(typeof when === "function" ? when() : when) !== not, [when, not]);
    const on_success = useCallback((): ReactNode => show?.() ?? children, [show, children]);
    const on_failure = useCallback(() => (typeof otherwise === "function" ? otherwise() : otherwise), [otherwise]);

    return <>{should_show ? on_success() : on_failure()}</>;
};
